import { pickDeductFund } from '../budget/settings';
import { updateMutation } from '../mutations';
import { defineRule } from './engine';

/** A time strictly after `after`, so the rule's update wins last-write-wins over the insert it follows. */
function laterThan(now: string, after: string): string {
  const t = Date.parse(after);
  return Date.parse(now) > t ? now : new Date(t + 1).toISOString();
}

/**
 * Spec §8: expense.created -> deduct from a fund (if Budget is on and either the expense has a fund,
 * or auto-deduct is on, in which case use the default fund). Fund balances are derived from the
 * ledger, so only the expense's fundId matters. Producers write the fund into the expense insert
 * themselves (resolveDeductFund), so when event.fundId is set the rule only marks the connection
 * (Spending ┄ Budget toast). A producer that couldn't resolve it (fundId null while a default
 * applies) gets a follow-up update, written after the insert. Payments expenses never get the
 * default fund (only the payee's own), but still show the connection. Runs only for new expenses, so
 * turning auto-deduct off or on never changes past entries.
 */
export const budgetDeductRule = defineRule<'expense.created'>({
  id: 'budget.deduct',
  on: 'expense.created',
  touches: ['spending', 'budget'],
  run(event, ctx) {
    if (event.fundId) return { mutations: [], connected: true };
    // Payments expenses (bills, loans, installments) keep the payee's fund or none: they are
    // already held back in what's left as commitments, so the default fund never takes them.
    if (event.sourceModule === 'payments') return { mutations: [], connected: true };
    const fundId = pickDeductFund(null, ctx.read.getBudgetSettings());
    if (!fundId) return [];
    return [
      updateMutation({
        table: 'ledger_entries',
        rowId: event.expenseId,
        values: { fundId },
        source: 'budget',
        at: laterThan(ctx.now, event.at),
      }),
    ];
  },
});
